import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  Database,
  RefreshCw,
  FolderPlus,
  Upload,
  Edit3,
  Trash2,
  Eye,
  Check,
  X,
  Folder,
  FileImage,
  Table,
  ChevronDown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag as TagIcon,
  Image as ImageIcon,
  Info,
  RotateCcw,
  Palette,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useBackgroundTasks } from "@/contexts/BackgroundTaskContext";
import { useCategoryContext } from "@/contexts/CategoryContext";
import { useTranslation } from "react-i18next";
import { apiService } from "@/services/api";
import { formatClassNameForDisplay } from "@/utils/classNameUtils";
import { getClassColor } from "@/utils/colorUtils";
import { cn } from "@/utils/cn";
import AuthenticatedImage from "@/components/AuthenticatedImage";
import CategoryManagementDialog from "@/components/CategoryManagementDialog";
import ThumbnailSelectionDialog from "@/components/ThumbnailSelectionDialog";
import ThumbnailPreview from "@/components/ThumbnailPreview";
import ClassEditDialog from "@/components/ClassEditDialog";
import { useIsMobile } from "@/hooks/useMediaQuery";
import MobileCatalogView from "@/components/catalog/mobile/MobileCatalogView";

interface ClassItem {
  id: string;
  folderName: string;
  displayName: string;
  imageCount: number;
  lastModified: string;
  selected?: boolean;
  tag?: string | null;
}

interface ImageItem {
  id: string;
  fileName: string;
  filePath: string;
  imageUrl: string;
  size: number;
  lastModified: string;
  selected?: boolean;
}

interface IndexStatus {
  isBuilding: boolean;
  lastBuilt: string | null;
  totalClasses: number;
  totalImages: number;
  embeddingCount: number;
}

const CatalogPage: React.FC = () => {
  const { isAdmin } = useAuth();
  const { isIndexRebuilding, startRebuildTask } = useBackgroundTasks();
  const isMobile = useIsMobile();
  const {
    getClassCategory,
    getClassThumbnail,
    refreshAll: refreshCategories,
    categories,
    refreshCategories: refreshCategoryList,
    refreshThumbnails,
    refreshThumbnailForClass,
  } = useCategoryContext();
  const [classes, setClasses] = useState<ClassItem[]>([]);
  // URL state management for selected class
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedClass, setSelectedClass] = useState<string | null>(
    searchParams.get("class") || null,
  );

  // Update URL when class selection changes
  const selectClass = useCallback(
    (classId: string | null) => {
      setSelectedClass(classId);
      if (classId) {
        setSearchParams({ class: classId });
      } else {
        setSearchParams({});
      }
    },
    [setSearchParams],
  );
  const [images, setImages] = useState<ImageItem[]>([]);
  const [selectionModeClasses, setSelectionModeClasses] = useState(false);
  const [selectionModeImages, setSelectionModeImages] = useState(false);
  const [infoOpenFor, setInfoOpenFor] = useState<string | null>(null);
  const [embeddingInfo, setEmbeddingInfo] = useState<Record<string, { dims: number; vector: number[] }>>({});

  // Close info popovers on outside click
  useEffect(() => {
    if (!infoOpenFor) return;
    const handler = () => setInfoOpenFor(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [infoOpenFor]);
  const [indexStatus, setIndexStatus] = useState<IndexStatus>({
    isBuilding: false,
    lastBuilt: null,
    totalClasses: 0,
    totalImages: 0,
    embeddingCount: 0,
  });

  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);

  // Selection states
  const [selectAllClasses, setSelectAllClasses] = useState(false);
  const [selectAllImages, setSelectAllImages] = useState(false);

  // Modal states
  const [showCreateClass, setShowCreateClass] = useState(false);
  const [newClassName, setNewClassName] = useState("");
  const [showImagePreview, setShowImagePreview] = useState<{
    id: string;
    url: string;
  } | null>(null);

  // Category and thumbnail dialog states
  const [showCategoryManagement, setShowCategoryManagement] = useState(false);
  const [showThumbnailSelection, setShowThumbnailSelection] = useState<{
    classId: string;
    className: string;
  } | null>(null);
  const [openCategoryMenuFor, setOpenCategoryMenuFor] = useState<string | null>(null);
  const [showClassEdit, setShowClassEdit] = useState<{ classId: string } | null>(null);
  const [showNewDropdown, setShowNewDropdown] = useState(false);
  const newDropdownRef = useRef<HTMLDivElement>(null);

  // Bulk action states
  const [showBulkCategoryDropdown, setShowBulkCategoryDropdown] = useState(false);
  const [showBulkTagInput, setShowBulkTagInput] = useState(false);
  const [bulkTagValue, setBulkTagValue] = useState("");
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const bulkCategoryRef = useRef<HTMLDivElement>(null);
  const bulkTagRef = useRef<HTMLDivElement>(null);

  // ZIP import states
  const [showZipRenameModal, setShowZipRenameModal] = useState(false);
  const [zipRenameError, setZipRenameError] = useState<string | null>(null);
  const [zipFileName, setZipFileName] = useState("");
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [zipFiles, setZipFiles] = useState<File[]>([]);
  const zipInputRef = useRef<HTMLInputElement>(null);

  // Upload progress states
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
  });
  // Multi-ZIP progress states
  const [zipProgress, setZipProgress] = useState({
    current: 0,
    total: 0,
    currentZipName: "",
  });

  // Drag and drop states
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  // Filter states
  const [classFilter, setClassFilter] = useState("");
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);

  // Sorting states
  const [sortBy, setSortBy] = useState<"lastModified" | "imageCount" | "name">(
    (localStorage.getItem("catalog-sort-by") as
      | "lastModified"
      | "imageCount"
      | "name") || "lastModified",
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    (localStorage.getItem("catalog-sort-order") as "asc" | "desc") || "desc",
  );
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);

  // Get current image index and navigation functions
  const currentImageIndex = useMemo(() => {
    if (!showImagePreview) return -1;
    return images.findIndex((img) => img.id === showImagePreview.id);
  }, [showImagePreview, images]);

  const navigateToImage = useCallback(
    (direction: "prev" | "next") => {
      if (currentImageIndex === -1 || images.length === 0) return;

      let newIndex;
      if (direction === "prev") {
        newIndex =
          currentImageIndex > 0 ? currentImageIndex - 1 : images.length - 1;
      } else {
        newIndex =
          currentImageIndex < images.length - 1 ? currentImageIndex + 1 : 0;
      }

      const newImage = images[newIndex];
      setShowImagePreview({ id: newImage.id, url: newImage.imageUrl });
    },
    [currentImageIndex, images],
  );

  // Keyboard event handler for image navigation and dropdown closing
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showImagePreview) return;

      switch (e.key) {
        case "Escape":
          setShowImagePreview(null);
          break;
        case "ArrowLeft":
          e.preventDefault();
          navigateToImage("prev");
          break;
        case "ArrowRight":
          e.preventDefault();
          navigateToImage("next");
          break;
      }
    };

    if (showImagePreview) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [showImagePreview, navigateToImage]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showSortDropdown &&
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(e.target as Node)
      ) {
        setShowSortDropdown(false);
      }
    };

    if (showSortDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showSortDropdown]);

  // Parquet viewer states
  const [showParquetViewer, setShowParquetViewer] = useState(false);
  const [parquetData, setParquetData] = useState<any>(null);
  const [loadingParquet, setLoadingParquet] = useState(false);

  // Close new dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        newDropdownRef.current &&
        !newDropdownRef.current.contains(event.target as Node)
      ) {
        setShowNewDropdown(false);
      }
    };

    if (showNewDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showNewDropdown]);

  // Close bulk action dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bulkCategoryRef.current && !bulkCategoryRef.current.contains(event.target as Node)) {
        setShowBulkCategoryDropdown(false);
      }
      if (bulkTagRef.current && !bulkTagRef.current.contains(event.target as Node)) {
        setShowBulkTagInput(false);
      }
    };

    if (showBulkCategoryDropdown || showBulkTagInput) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showBulkCategoryDropdown, showBulkTagInput]);

  // Warn user before closing page during upload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isUploading) {
        e.preventDefault();
        e.returnValue = "Upload in progress. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isUploading]);

  // Load classes and index status
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [classesData, statusData, classTags] = await Promise.all([
        apiService.getEnrollmentClasses(),
        apiService.getIndexStatus(),
        apiService.getAllClassTags(),
      ]);

      // Map classes with selected: false and tag from classTags
      const mappedClasses = classesData.map((cls: any) => ({
        ...cls,
        selected: false,
        tag: classTags[cls.folderName] || null,
      }));

      setClasses(mappedClasses);
      setIndexStatus(statusData);
      setError(null);

      // Force refresh thumbnails to trigger auto-select for classes without thumbnails
      refreshThumbnails(true);
    } catch (err) {
      setError("Failed to load data");
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  }, [refreshThumbnails]);

  // Load images for selected class
  const loadClassImages = useCallback(async (classId: string) => {
    try {
      const imagesData = await apiService.getClassImages(classId);
      setImages(
        imagesData.map((img: any) => ({
          ...img,
          selected: false,
        })),
      );
    } catch (err) {
      console.error("Error loading class images:", err);
      setImages([]);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Open first class by default after classes load if none selected (desktop only)
  useEffect(() => {
    if (!isMobile && !selectedClass && classes.length > 0) {
      selectClass(classes[0].id)
    }
  }, [classes, selectedClass, selectClass, isMobile])

  useEffect(() => {
    if (selectedClass) {
      loadClassImages(selectedClass);
    } else {
      setImages([]);
    }
  }, [selectedClass, loadClassImages]);

  // Selection handlers
  const toggleClassSelection = (classId: string) => {
    setClasses((prev) =>
      prev.map((cls) =>
        cls.id === classId ? { ...cls, selected: !cls.selected } : cls,
      ),
    );
  };

  const toggleAllClassSelection = () => {
    const newSelectAll = !selectAllClasses;
    setSelectAllClasses(newSelectAll);
    setClasses((prev) =>
      prev.map((cls) => ({ ...cls, selected: newSelectAll })),
    );
  };

  const toggleImageSelection = (imageId: string) => {
    setImages((prev) =>
      prev.map((img) =>
        img.id === imageId ? { ...img, selected: !img.selected } : img,
      ),
    );
  };

  const toggleAllImageSelection = () => {
    const newSelectAll = !selectAllImages;
    setSelectAllImages(newSelectAll);
    setImages((prev) =>
      prev.map((img) => ({ ...img, selected: newSelectAll })),
    );
  };

  // Tag update handler
  const handleTagUpdate = async () => {
    // Reload tags for all classes
    try {
      const classTags = await apiService.getAllClassTags();
      setClasses((prev) =>
        prev.map((cls) => ({
          ...cls,
          tag: classTags[cls.folderName] || null,
        }))
      );
    } catch (err) {
      console.error("Failed to reload tags:", err);
    }
  };

  // Bulk action handlers
  const handleBulkAssignCategory = async (category: { name: string; color: string }) => {
    const selectedClassIds = classes.filter(cls => cls.selected).map(cls => cls.id);
    if (selectedClassIds.length === 0) return;

    try {
      setBulkActionLoading(true);
      await apiService.bulkAssignCategory(selectedClassIds, category);
      // Force refresh to bypass cache
      await refreshCategories(true);
      setShowBulkCategoryDropdown(false);
    } catch (err) {
      console.error("Failed to bulk assign category:", err);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkAssignTag = async () => {
    const selectedClassIds = classes.filter(cls => cls.selected).map(cls => cls.id);
    if (selectedClassIds.length === 0 || !bulkTagValue.trim()) return;

    try {
      setBulkActionLoading(true);
      await apiService.bulkAssignTag(selectedClassIds, bulkTagValue.trim());
      await handleTagUpdate();
      setShowBulkTagInput(false);
      setBulkTagValue("");
    } catch (err) {
      console.error("Failed to bulk assign tag:", err);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkReset = async () => {
    const selectedClassIds = classes.filter(cls => cls.selected).map(cls => cls.id);
    if (selectedClassIds.length === 0) return;

    if (!confirm(`Reset ${selectedClassIds.length} classe(s)? This will clear their tags, categories, reset thumbnails to first image, and recalculate embeddings.`)) {
      return;
    }

    try {
      setBulkActionLoading(true);
      await apiService.bulkResetClasses(selectedClassIds);
      // Force refresh to bypass cache and show updated state
      await Promise.all([
        refreshCategories(true),
        handleTagUpdate(),
        refreshThumbnails(true),
        loadData(),
      ]);
    } catch (err) {
      console.error("Failed to bulk reset:", err);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // CRUD operations
  const createClass = async () => {
    if (!newClassName.trim()) return;

    try {
      const newClass = await apiService.createClass({
        folderName: newClassName,
        displayName: newClassName, // displayName same as folderName now
      });

      setClasses((prev) => [...prev, { ...newClass, selected: false }]);
      setShowCreateClass(false);
      setNewClassName("");

      // Update index stats in real-time
      setIndexStatus((prev) => ({
        ...prev,
        totalClasses: prev.totalClasses + 1,
      }));

      // Reload final index status to ensure accuracy
      const statusData = await apiService.getIndexStatus();
      setIndexStatus(statusData);
    } catch (err) {
      console.error("Failed to create class:", err);
    }
  };

  const handleZipSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const zipFilesArray = Array.from(files).filter(file =>
      file.name.toLowerCase().endsWith('.zip')
    );

    if (zipFilesArray.length === 0) {
      alert('Please select ZIP file(s)');
      return;
    }

    if (zipFilesArray.length === 1) {
      // Single ZIP - use existing flow
      setZipFile(zipFilesArray[0]);
      importZipFile(zipFilesArray[0]);
    } else {
      // Multiple ZIPs - use batch import
      setZipFiles(zipFilesArray);
      importMultipleZipFiles(zipFilesArray);
    }
  };

  const importZipFile = async (file: File, customClassName?: string) => {
    try {
      setIsUploading(true);
      setUploadProgress({ current: 0, total: 100 });

      const result = await apiService.importZipClass(file, customClassName);

      // Show upload progress modal
      setUploadProgress({ current: 0, total: result.total_files });

      // Poll for task completion
      await pollUploadTask(result.task_id, result.total_files);

      // Reset state
      setZipFile(null);
      setZipFileName("");
      setShowZipRenameModal(false);
      setZipRenameError(null);

      // Reload classes and index status
      await loadData();

      // Navigate to the new class page instead of showing alert
      selectClass(result.class_name);
    } catch (err: any) {
      setIsUploading(false);

      // Check if it's a class name conflict (409 status)
      if (err.response?.status === 409) {
        const errorMessage = err.response?.data?.detail || '';
        setZipRenameError(errorMessage);

        // Extract suggested name from error message
        const match = errorMessage.match(/Try '([^']+)' instead/);
        const suggestedName = match ? match[1] : file.name.replace('.zip', '') + '_2';

        setZipFileName(suggestedName);
        setShowZipRenameModal(true);
      } else {
        console.error("Failed to import ZIP:", err);
        alert(`Failed to import ZIP: ${err.response?.data?.detail || err.message}`);
      }
    } finally {
      // Reset file input
      if (zipInputRef.current) {
        zipInputRef.current.value = '';
      }
    }
  };

  const handleZipRename = () => {
    if (!zipFile || !zipFileName.trim()) return;

    setShowZipRenameModal(false);
    importZipFile(zipFile, zipFileName.trim());
  };

  const importMultipleZipFiles = async (files: File[]) => {
    let lastClassName = "";

    try {
      setIsUploading(true);
      setZipProgress({ current: 0, total: files.length, currentZipName: "" });
      setUploadProgress({ current: 0, total: 0 });

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const zipName = file.name.replace('.zip', '');

        setZipProgress({
          current: i,
          total: files.length,
          currentZipName: zipName,
        });
        setUploadProgress({ current: 0, total: 100 });

        try {
          const result = await apiService.importZipClass(file);
          lastClassName = result.class_name;

          // Update image progress for this ZIP
          setUploadProgress({ current: 0, total: result.total_files });

          // Poll for task completion (keepUploading=true to not hide modal)
          await pollUploadTask(result.task_id, result.total_files, true);

        } catch (err: any) {
          // Handle conflict - skip or auto-rename
          if (err.response?.status === 409) {
            const errorMessage = err.response?.data?.detail || '';
            const match = errorMessage.match(/Try '([^']+)' instead/);
            const suggestedName = match ? match[1] : `${zipName}_2`;

            // Auto-retry with suggested name
            try {
              const retryResult = await apiService.importZipClass(file, suggestedName);
              lastClassName = retryResult.class_name;
              setUploadProgress({ current: 0, total: retryResult.total_files });
              await pollUploadTask(retryResult.task_id, retryResult.total_files, true);
            } catch (retryErr) {
              console.error(`Failed to import ${file.name} even with rename:`, retryErr);
            }
          } else {
            console.error(`Failed to import ${file.name}:`, err);
          }
        }
      }

      // Final progress update
      setZipProgress({ current: files.length, total: files.length, currentZipName: "" });

      // Reset state
      setZipFiles([]);

      // Reload classes and index status
      await loadData();

      // Navigate to the last processed class
      if (lastClassName) {
        selectClass(lastClassName);
      }

    } catch (err) {
      console.error("Failed to import multiple ZIPs:", err);
    } finally {
      setIsUploading(false);
      setZipProgress({ current: 0, total: 0, currentZipName: "" });
      setUploadProgress({ current: 0, total: 0 });

      // Reset file input
      if (zipInputRef.current) {
        zipInputRef.current.value = '';
      }
    }
  };

  const pollUploadTask = async (taskId: string, totalFiles: number, keepUploading: boolean = false) => {
    const maxAttempts = 300; // 5 minutes max (1 second intervals)
    let attempts = 0;

    return new Promise<void>((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          attempts++;
          const status = await apiService.getTaskStatus(taskId);

          if (status.progress !== undefined) {
            setUploadProgress({
              current: Math.round(status.progress * totalFiles),
              total: totalFiles,
            });
          }

          if (status.status === "completed") {
            clearInterval(interval);
            if (!keepUploading) setIsUploading(false);
            resolve();
          } else if (status.status === "failed") {
            clearInterval(interval);
            if (!keepUploading) setIsUploading(false);
            reject(new Error(status.error || "Task failed"));
          } else if (attempts >= maxAttempts) {
            clearInterval(interval);
            if (!keepUploading) setIsUploading(false);
            reject(new Error("Task timed out"));
          }
        } catch (err) {
          console.error("Error polling task status:", err);
          clearInterval(interval);
          if (!keepUploading) setIsUploading(false);
          reject(err);
        }
      }, 1000);
    });
  };

  const deleteSelectedClasses = async () => {
    const selectedClassIds = classes
      .filter((cls) => cls.selected)
      .map((cls) => cls.id);
    if (selectedClassIds.length === 0) return;

    if (
      !confirm(
        `Delete ${selectedClassIds.length} classe(s)? This will also delete all images in them.`,
      )
    )
      return;

    try {
      // Calculate total images to be deleted
      const deletedImageCount = classes
        .filter((cls) => selectedClassIds.includes(cls.id))
        .reduce((sum, cls) => sum + cls.imageCount, 0);

      await Promise.all(
        selectedClassIds.map((id) => apiService.deleteClass(id)),
      );

      setClasses((prev) => prev.filter((cls) => !cls.selected));
      if (selectedClassIds.includes(selectedClass || "")) {
        selectClass(null);
      }

      // Update index stats in real-time
      setIndexStatus((prev) => ({
        ...prev,
        totalClasses: prev.totalClasses - selectedClassIds.length,
        totalImages: prev.totalImages - deletedImageCount,
        embeddingCount: prev.embeddingCount - deletedImageCount,
      }));

      // Reload final index status to ensure accuracy
      const statusData = await apiService.getIndexStatus();
      setIndexStatus(statusData);
    } catch (err) {
      console.error("Failed to delete classes:", err);
    }
  };

  const deleteSelectedImages = async () => {
    const selectedImageIds = images
      .filter((img) => img.selected)
      .map((img) => img.id);
    if (selectedImageIds.length === 0) return;

    if (!confirm(`Delete ${selectedImageIds.length} image(s)?`)) return;

    try {
      await Promise.all(
        selectedImageIds.map((id) => apiService.deleteImage(id)),
      );

      setImages((prev) => prev.filter((img) => !img.selected));

      // Update class image count
      setClasses((prev) =>
        prev.map((cls) =>
          cls.id === selectedClass
            ? { ...cls, imageCount: cls.imageCount - selectedImageIds.length }
            : cls,
        ),
      );

      // Update index stats in real-time
      setIndexStatus((prev) => ({
        ...prev,
        totalImages: prev.totalImages - selectedImageIds.length,
        embeddingCount: prev.embeddingCount - selectedImageIds.length,
      }));

      // Reload final index status to ensure accuracy
      const statusData = await apiService.getIndexStatus();
      setIndexStatus(statusData);
    } catch (err) {
      console.error("Failed to delete images:", err);
    }
  };

  const initializeEmbeddings = async () => {
    if (
      !confirm(
        "Initialize embeddings from existing enrollment data? This will process all images in the enrollment directory.",
      )
    )
      return;

    try {
      setIndexStatus((prev) => ({ ...prev, isBuilding: true }));
      const result = await apiService.initializeEmbeddingIndex();

      console.log("Initialization result:", result);
      alert(
        `Initialization complete! Processed ${result.stats.successful}/${result.stats.total_processed} images successfully.`,
      );

      // Reload status after initialization
      const statusData = await apiService.getIndexStatus();
      setIndexStatus(statusData);
    } catch (err) {
      console.error("Failed to initialize embeddings:", err);
      alert("Failed to initialize embeddings. Check the console for details.");
      setIndexStatus((prev) => ({ ...prev, isBuilding: false }));
    }
  };

  const forceRebuildIndex = async () => {
    if (
      !confirm(
        "Force rebuild the entire embedding index? This will run in the background and may take several minutes.",
      )
    )
      return;

    try {
      await startRebuildTask();

      // Reload status after starting background task
      const statusData = await apiService.getIndexStatus();
      setIndexStatus(statusData);
    } catch (err) {
      console.error("Failed to start rebuild task:", err);
      alert("Failed to start rebuild. Check the console for details.");
    }
  };

  const loadParquetData = async () => {
    try {
      setLoadingParquet(true);
      const data = await apiService.getEmbeddingParquetData();
      setParquetData(data);
      setShowParquetViewer(true);
    } catch (err) {
      console.error("Failed to load parquet data:", err);
      alert("Failed to load parquet data. Check console for details.");
    } finally {
      setLoadingParquet(false);
    }
  };

  const viewEmbeddingVector = async (itemId: string) => {
    try {
      const vectorData = await apiService.getEmbeddingVector(itemId);

      // Create a new window/tab with the embedding data
      const newWindow = window.open("", "_blank");
      if (newWindow) {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Embedding Vector - ${itemId}</title>
            <style>
              body { 
                font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace; 
                padding: 20px; 
                background: #f8f9fa;
                margin: 0;
              }
              .header {
                background: white;
                padding: 20px;
                border-radius: 8px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                margin-bottom: 20px;
              }
              .vector-container {
                background: white;
                padding: 20px;
                border-radius: 8px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              }
              .vector-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
                gap: 8px;
                margin-top: 15px;
              }
              .vector-value {
                padding: 4px 8px;
                background: #f1f3f4;
                border-radius: 4px;
                text-align: center;
                font-size: 12px;
              }
              .info-item {
                margin: 5px 0;
              }
              .label {
                font-weight: bold;
                color: #333;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Embedding Vector</h1>
              <div class="info-item"><span class="label">Item ID:</span> ${vectorData.item_id}</div>
              <div class="info-item"><span class="label">Class:</span> ${vectorData.class_name}</div>
              <div class="info-item"><span class="label">File:</span> ${vectorData.file_path}</div>
              <div class="info-item"><span class="label">Dimensions:</span> ${vectorData.embedding_dimensions}</div>
            </div>
            <div class="vector-container">
              <h3>Vector Values (${vectorData.embedding_dimensions} dimensions):</h3>
              <div class="vector-grid">
                ${vectorData.embedding_vector
                  .map(
                    (value: number, index: number) => `
                  <div class="vector-value" title="Index ${index}: ${value}">${Number(value).toFixed(6)}</div>
                `,
                  )
                  .join("")}
              </div>
            </div>
          </body>
          </html>
        `);
        newWindow.document.close();
      }
    } catch (err) {
      console.error("Failed to load embedding vector:", err);
      alert("Failed to load embedding vector. Check console for details.");
    }
  };

  // Generic function to handle file uploads from any source
  const uploadFiles = async (files: File[]) => {
    if (!selectedClass || isIndexRebuilding) return;

    try {
      setIsUploading(true);
      setUploadProgress({ current: 0, total: files.length });
      setUploadedImages([]);

      // Upload all files at once (backend processes them in background)
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });

      const response = await apiService.uploadImages(selectedClass, formData);

      // Show success message - actual processing happens in background
      console.log(`Upload started: ${response.message}`);
      console.log(`Task ID: ${response.task_id}`);
      console.log(`Files uploaded: ${response.total_files}`);

      // Show feedback to user
      setUploadedImages(files.map((f) => f.name));
      setUploadProgress({ current: files.length, total: files.length });

      // Note: The actual embedding computation happens in background
      // The headbar indicator (admin only) will show progress
      // When task completes, reload the class images
      setTimeout(async () => {
        await loadClassImages(selectedClass);
        // Update classes list
        await loadData();
      }, 2000);

    } catch (err) {
      console.error("Failed to upload images:", err);
    } finally {
      setIsUploading(false);
      // Reset progress after a delay to show completion
      setTimeout(() => {
        setUploadProgress({ current: 0, total: 0 });
        setUploadedImages([]);
      }, 3000);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!event.target.files) return;
    const files = Array.from(event.target.files);
    await uploadFiles(files);
    // Reset file input
    event.target.value = "";
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter((prev) => prev - 1);
      if (dragCounter === 1) {
        setIsDragOver(false);
      }
    },
    [dragCounter],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      setDragCounter(0);

      if (!selectedClass || isIndexRebuilding) return;

      const files = Array.from(e.dataTransfer.files);

      // Filter for image files
      const imageFiles = files.filter((file) => {
        return (
          file.type.startsWith("image/") ||
          file.name.toLowerCase().endsWith(".heic") ||
          file.name.toLowerCase().endsWith(".heif")
        );
      });

      if (imageFiles.length === 0) {
        console.warn("No image files detected in dropped items");
        return;
      }

      console.log(`Dropped ${imageFiles.length} image files for upload`);
      await uploadFiles(imageFiles);
    },
    [selectedClass, isIndexRebuilding, uploadFiles],
  );

  // fzf-style fuzzy matching function
  const fuzzyMatch = useCallback((text: string, query: string): boolean => {
    if (!query) return true;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();

    let textIndex = 0;
    let queryIndex = 0;

    while (textIndex < lowerText.length && queryIndex < lowerQuery.length) {
      if (lowerText[textIndex] === lowerQuery[queryIndex]) {
        queryIndex++;
      }
      textIndex++;
    }

    return queryIndex === lowerQuery.length;
  }, []);

  // Handle sort change
  const handleSortChange = (
    newSortBy: "lastModified" | "imageCount" | "name",
  ) => {
    if (newSortBy === sortBy) {
      // If same sort field, toggle order
      const newOrder = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(newOrder);
      localStorage.setItem("catalog-sort-order", newOrder);
    } else {
      // If different sort field, use default order for that field
      setSortBy(newSortBy);
      localStorage.setItem("catalog-sort-by", newSortBy);

      // Set appropriate default order
      const defaultOrder = newSortBy === "name" ? "asc" : "desc";
      setSortOrder(defaultOrder);
      localStorage.setItem("catalog-sort-order", defaultOrder);
    }
    setShowSortDropdown(false);
  };

  // Toggle sort order
  const toggleSortOrder = () => {
    const newOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newOrder);
    localStorage.setItem("catalog-sort-order", newOrder);
  };

  // Get sort label
  const getSortLabel = () => {
    switch (sortBy) {
      case "lastModified":
        return "Modified Date";
      case "imageCount":
        return "Image Count";
      case "name":
        return "Name";
      default:
        return "Modified Date";
    }
  };

  // Sort and filter classes
  const sortedAndFilteredClasses = useMemo(() => {
    let filtered = classes;

    // Apply filter
    if (classFilter) {
      filtered = classes.filter(
        (cls) =>
          fuzzyMatch(cls.displayName, classFilter) ||
          fuzzyMatch(cls.folderName, classFilter) ||
          fuzzyMatch(cls.id, classFilter),
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case "lastModified":
          comparison =
            new Date(a.lastModified).getTime() -
            new Date(b.lastModified).getTime();
          break;
        case "imageCount":
          comparison = a.imageCount - b.imageCount;
          break;
        case "name":
          comparison = a.folderName.localeCompare(b.folderName);
          break;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [classes, classFilter, fuzzyMatch, sortBy, sortOrder]);

  const selectedClassesCount = sortedAndFilteredClasses.filter(
    (cls) => cls.selected,
  ).length;
  const selectedImagesCount = images.filter((img) => img.selected).length;

  // Delete single image
  const deleteImage = async (imageId: string) => {
    try {
      await apiService.deleteImage(imageId);
      setImages((prev) => prev.filter((img) => img.id !== imageId));

      // Update class image count
      setClasses((prev) =>
        prev.map((cls) =>
          cls.id === selectedClass
            ? { ...cls, imageCount: cls.imageCount - 1 }
            : cls,
        ),
      );

      // Update index stats
      setIndexStatus((prev) => ({
        ...prev,
        totalImages: prev.totalImages - 1,
        embeddingCount: prev.embeddingCount - 1,
      }));

      // Reload final index status
      const statusData = await apiService.getIndexStatus();
      setIndexStatus(statusData);
    } catch (err) {
      console.error("Failed to delete image:", err);
    }
  };

  // Delete single class
  const deleteClass = async (classId: string) => {
    try {
      const classToDelete = classes.find((cls) => cls.id === classId);
      if (!classToDelete) return;

      await apiService.deleteClass(classId);

      setClasses((prev) => prev.filter((cls) => cls.id !== classId));
      if (selectedClass === classId) {
        selectClass(null);
      }

      // Update index stats
      setIndexStatus((prev) => ({
        ...prev,
        totalClasses: prev.totalClasses - 1,
        totalImages: prev.totalImages - classToDelete.imageCount,
        embeddingCount: prev.embeddingCount - classToDelete.imageCount,
      }));

      // Reload final index status
      const statusData = await apiService.getIndexStatus();
      setIndexStatus(statusData);
    } catch (err) {
      console.error("Failed to delete class:", err);
    }
  };

  // Set thumbnail
  const setThumbnail = async (classId: string, fileName: string) => {
    try {
      await apiService.setClassThumbnail(classId, fileName);
      refreshThumbnailForClass(classId);
    } catch (err) {
      console.error("Failed to set thumbnail:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Mobile View
  if (isMobile) {
    return (
      <div className="h-[calc(100vh-4rem-5rem)] -mx-4 -mt-4">
        <MobileCatalogView
          classes={classes}
          images={images}
          selectedClass={selectedClass}
          indexStatus={indexStatus}
          isAdmin={isAdmin}
          loading={loading}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          categories={categories}
          getClassCategory={getClassCategory}
          getClassThumbnail={getClassThumbnail}
          classFilter={classFilter}
          sortBy={sortBy}
          sortOrder={sortOrder}
          sortedAndFilteredClasses={sortedAndFilteredClasses}
          onSelectClass={selectClass}
          onCreateClass={async (name) => {
            try {
              const newClass = await apiService.createClass({
                folderName: name,
                displayName: name,
              });
              setClasses((prev) => [...prev, { ...newClass, selected: false }]);
              setIndexStatus((prev) => ({
                ...prev,
                totalClasses: prev.totalClasses + 1,
              }));
              const statusData = await apiService.getIndexStatus();
              setIndexStatus(statusData);
            } catch (err) {
              console.error("Failed to create class:", err);
              throw err;
            }
          }}
          onDeleteClass={deleteClass}
          onUploadFiles={uploadFiles}
          onDeleteImage={deleteImage}
          onSetThumbnail={setThumbnail}
          onRefresh={loadData}
          onFilterChange={setClassFilter}
          onSortChange={handleSortChange}
          onToggleSortOrder={toggleSortOrder}
          onImportZip={(file) => importZipFile(file)}
          onCategoryChange={refreshCategories}
          onTagUpdate={handleTagUpdate}
          onInitializeEmbeddings={initializeEmbeddings}
          onForceRebuild={forceRebuildIndex}
          isIndexRebuilding={isIndexRebuilding}
        />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col space-y-4">
      {/* Header & Toolbar */}
      <div className="bg-white rounded-lg shadow-sm border px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Database className="h-8 w-8 text-primary-600" />
            <div>
              <h1 className="text-xl font-serif font-semibold text-gray-900">
                Catalog
              </h1>
              <p className="text-sm text-gray-500">
                Manage luxury inventory articles
              </p>
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex items-center space-x-4">
            {/* Index Status */}
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <div className="text-center">
                <div className="font-semibold text-primary-600">
                  {indexStatus.totalClasses}
                </div>
                <div className="text-xs text-gray-500">Classes</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-primary-600">
                  {indexStatus.totalImages}
                </div>
                <div className="text-xs text-gray-500">Images</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-primary-600">
                  {indexStatus.embeddingCount}
                </div>
                <div className="text-xs text-gray-500">Embeddings</div>
              </div>
              <div className="text-center">
                <div
                  className={`text-xs font-medium px-2 py-1 rounded-full ${indexStatus.isBuilding ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}`}
                >
                  {indexStatus.isBuilding ? "Building..." : "Ready"}
                </div>
              </div>
            </div>

            <div className="h-8 border-l border-gray-200"></div>

            {/* Action Buttons - Admin Only */}
            {isAdmin && (
                    <div className="flex items-center space-x-2">
                <button
                  onClick={loadData}
                  className="flex items-center justify-center w-10 h-10 rounded-lg border transition-all bg-white border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>

                {/* Show Initialize button if no embeddings exist */}
                {indexStatus.totalClasses === 0 &&
                  indexStatus.totalImages === 0 && (
                    <button
                      onClick={initializeEmbeddings}
                      disabled={indexStatus.isBuilding}
                      className={cn(
                        "flex items-center justify-center w-10 h-10 rounded-lg border transition-all",
                        indexStatus.isBuilding
                          ? "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
                          : "bg-green-100 border-green-300 text-green-700 hover:bg-green-200 hover:border-green-400",
                      )}
                    >
                      <Database className="h-4 w-4" />
                    </button>
                  )}

                <button
                  onClick={forceRebuildIndex}
                  disabled={indexStatus.isBuilding || isIndexRebuilding}
                  className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-lg border transition-all",
                    indexStatus.isBuilding || isIndexRebuilding
                      ? "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
                      : "bg-red-100 border-red-300 text-red-700 hover:bg-red-200 hover:border-red-400",
                  )}
                >
                  <Database className="h-4 w-4" />
                </button>

                <button
                  onClick={loadParquetData}
                  disabled={loadingParquet}
                  className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-lg border transition-all",
                    loadingParquet
                      ? "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
                      : "bg-blue-100 border-blue-300 text-blue-700 hover:bg-blue-200 hover:border-blue-400",
                  )}
                >
                  <Table className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-grow flex space-x-6 overflow-hidden">
        {/* Classes Panel */}
        <div className="w-1/3 bg-white shadow rounded-lg flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-gray-900">
                  Classes
                </h2>
                {classes.length > 0 && (
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectionModeClasses}
                      onChange={() => setSelectionModeClasses(v => !v)}
                      className="rounded border-purple-300 text-purple-600 shadow-sm focus:border-purple-300 focus:ring focus:ring-purple-200 focus:ring-opacity-50"
                    />
                    <span className="text-sm text-gray-500">
                      {sortedAndFilteredClasses.length}/{classes.length}
                    </span>
                  </label>
                )}
              </div>

              {/* Only show Categories and New buttons when NOT in selection mode */}
              {!selectionModeClasses && (
                <div className="flex items-center gap-2">
                  {/* Categories button */}
                  <button
                    onClick={() => setShowCategoryManagement(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    title="Manage categories"
                  >
                    <TagIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">Categories</span>
                  </button>

                  {/* Primary action dropdown */}
                  <div className="relative" ref={newDropdownRef}>
                    <button
                      onClick={() => setShowNewDropdown(!showNewDropdown)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                    >
                      <FolderPlus className="h-4 w-4" />
                      New
                      <ChevronDown className="h-3 w-3" />
                    </button>

                    {showNewDropdown && (
                      <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-300 rounded-lg shadow-lg z-10 overflow-hidden">
                        <button
                          onClick={() => {
                            setShowCreateClass(true);
                            setShowNewDropdown(false);
                          }}
                          className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors flex items-center gap-3"
                        >
                          <FolderPlus className="h-4 w-4 text-primary-600" />
                          <div>
                            <div className="font-medium text-gray-900">Create empty class</div>
                            <div className="text-xs text-gray-500">Start with a new folder</div>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            zipInputRef.current?.click();
                            setShowNewDropdown(false);
                          }}
                          className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors flex items-center gap-3 border-t border-gray-200"
                        >
                          <Upload className="h-4 w-4 text-blue-600" />
                          <div>
                            <div className="font-medium text-gray-900">Import from archive</div>
                            <div className="text-xs text-gray-500">Upload ZIP file with classes</div>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>

                  <input
                    ref={zipInputRef}
                    type="file"
                    accept=".zip"
                    multiple
                    onChange={handleZipSelect}
                    className="hidden"
                  />
                </div>
              )}
            </div>

            {/* Select all and Actions row - shown when in selection mode */}
            {selectionModeClasses && (
              <div className="mt-3 space-y-3">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectAllClasses}
                    onChange={toggleAllClassSelection}
                    className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-600">Select all</span>
                </label>

                {selectedClassesCount > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Assign Category Dropdown */}
                    <div className="relative" ref={bulkCategoryRef}>
                      <button
                        onClick={() => {
                          setShowBulkCategoryDropdown(!showBulkCategoryDropdown);
                          setShowBulkTagInput(false);
                        }}
                        disabled={bulkActionLoading}
                        className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                      >
                        <Palette className="h-4 w-4" />
                        Category
                        <ChevronDown className="h-3 w-3" />
                      </button>
                      {showBulkCategoryDropdown && (
                        <div className="absolute left-0 mt-1 w-48 bg-white border border-gray-300 rounded-lg shadow-lg z-20 py-1 max-h-64 overflow-y-auto">
                          {Object.keys(categories).length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">No categories defined</div>
                          ) : (
                            Object.values(categories).map((cat) => (
                              <button
                                key={cat.name}
                                onClick={() => handleBulkAssignCategory({ name: cat.name, color: cat.color })}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                              >
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: cat.color }}
                                />
                                {cat.name}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    {/* Assign Tag Input */}
                    <div className="relative" ref={bulkTagRef}>
                      <button
                        onClick={() => {
                          setShowBulkTagInput(!showBulkTagInput);
                          setShowBulkCategoryDropdown(false);
                        }}
                        disabled={bulkActionLoading}
                        className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                      >
                        <TagIcon className="h-4 w-4" />
                        Tag
                        <ChevronDown className="h-3 w-3" />
                      </button>
                      {showBulkTagInput && (
                        <div className="absolute left-0 mt-1 w-56 bg-white border border-gray-300 rounded-lg shadow-lg z-20 p-3">
                          <input
                            type="text"
                            value={bulkTagValue}
                            onChange={(e) => setBulkTagValue(e.target.value)}
                            placeholder="Enter tag..."
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleBulkAssignTag();
                            }}
                            autoFocus
                          />
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={handleBulkAssignTag}
                              disabled={!bulkTagValue.trim()}
                              className="flex-1 px-2 py-1.5 text-xs font-medium rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                            >
                              Assign
                            </button>
                            <button
                              onClick={() => {
                                setShowBulkTagInput(false);
                                setBulkTagValue("");
                              }}
                              className="px-2 py-1.5 text-xs font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Reset Button */}
                    <button
                      onClick={handleBulkReset}
                      disabled={bulkActionLoading}
                      className="inline-flex items-center gap-1.5 px-3 py-2 border border-orange-200 text-sm font-medium rounded-lg text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors disabled:opacity-50"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Reset
                    </button>

                    {/* Delete Button */}
                    <button
                      onClick={deleteSelectedClasses}
                      disabled={bulkActionLoading}
                      className="inline-flex items-center gap-1.5 px-3 py-2 border border-red-200 text-sm font-medium rounded-lg text-red-700 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete ({selectedClassesCount})
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Combined Filter and Sort Controls */}
          <div className="px-6 py-3 border-b border-gray-200">
            <div className="flex items-center gap-2">
              {/* Filter Input with integrated sort controls */}
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Filter classes..."
                  value={classFilter}
                  onChange={(e) => setClassFilter(e.target.value)}
                  className="w-full pl-3 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Sort Dropdown */}
              <div className="relative" ref={sortDropdownRef}>
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                  title="Sort options"
                >
                  <ArrowUpDown className="h-4 w-4" />
                  <span className="hidden sm:inline">{getSortLabel()}</span>
                  <ChevronDown className="h-3 w-3" />
                </button>

                {showSortDropdown && (
                  <div className="absolute top-full right-0 mt-1 w-44 bg-white border border-gray-300 rounded-lg shadow-lg z-10 overflow-hidden">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSortChange("lastModified");
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${sortBy === "lastModified" ? "bg-primary-50 text-primary-700 font-medium" : "text-gray-700"}`}
                    >
                      Modified Date
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSortChange("imageCount");
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${sortBy === "imageCount" ? "bg-primary-50 text-primary-700 font-medium" : "text-gray-700"}`}
                    >
                      Image Count
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSortChange("name");
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${sortBy === "name" ? "bg-primary-50 text-primary-700 font-medium" : "text-gray-700"}`}
                    >
                      Name
                    </button>
                  </div>
                )}
              </div>

              {/* Sort Order Toggle */}
              <button
                onClick={toggleSortOrder}
                className="inline-flex items-center justify-center p-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                title={`Sort ${sortOrder === "asc" ? "ascending" : "descending"}`}
              >
                {sortOrder === "asc" ? (
                  <ArrowUp className="h-4 w-4" />
                ) : (
                  <ArrowDown className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <div className="h-full overflow-y-auto">
            {sortedAndFilteredClasses.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Folder className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                <p>
                  {classFilter
                    ? "No classes match your filter"
                    : "No classes found"}
                </p>
              </div>
            ) : classes.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Folder className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                <p>No classes found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {sortedAndFilteredClasses.map((cls) => (
                  <div
                    key={cls.id}
                    className={`p-3 hover:bg-gray-50 cursor-pointer transition-all ${
                      selectionModeClasses
                        ? cls.selected
                          ? "ring-2 ring-inset ring-blue-400 bg-blue-50/30"
                          : ""
                        : selectedClass === cls.id
                        ? "bg-primary-50"
                        : ""
                    }`}
                    onClick={() => {
                      if (selectionModeClasses) {
                        toggleClassSelection(cls.id);
                      } else {
                        selectClass(cls.id);
                      }
                    }}
                  >
                    <div className="flex items-center gap-3">
                      {selectionModeClasses && (
                        <input
                          type="checkbox"
                          checked={cls.selected || false}
                          onChange={() => {}} // Handled by parent click
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 flex-shrink-0 pointer-events-none"
                        />
                      )}

                      {/* Thumbnail - show full image with gray padding like image grid */}
                      {getClassThumbnail(cls.id)?.url ? (
                        <div className="flex-shrink-0 w-[60px] h-[60px] bg-gray-100 rounded border border-gray-200 flex items-center justify-center overflow-hidden">
                          <AuthenticatedImage
                            imageUrl={getClassThumbnail(cls.id)!.url}
                            alt="thumb"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="flex-shrink-0 w-[60px] h-[60px] flex items-center justify-center bg-gray-100 rounded border border-gray-200">
                          <Folder className="h-6 w-6 text-gray-400" />
                        </div>
                      )}

                      {/* Content - grows to fill available space */}
                      <div className="flex-1 min-w-0 flex flex-col gap-1">
                        {/* Row 1: Class Name */}
                        <ThumbnailPreview
                          className={cls.folderName}
                          thumbnailUrl={getClassThumbnail(cls.id)?.url}
                        >
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {formatClassNameForDisplay(cls.folderName)}
                          </p>
                        </ThumbnailPreview>

                        {/* Row 2: Category and Tag */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {getClassCategory(cls.id) && (
                            <div
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: `${getClassCategory(cls.id)?.color}20`,
                                color: getClassCategory(cls.id)?.color,
                              }}
                            >
                              {getClassCategory(cls.id)?.name}
                            </div>
                          )}
                          {cls.tag && (
                            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                              <TagIcon className="h-3 w-3" />
                              {cls.tag}
                            </div>
                          )}
                        </div>

                        {/* Row 3: Image count and modified date */}
                        <p className="text-xs text-gray-500">
                          {cls.imageCount} images • Modified{" "}
                          {new Date(cls.lastModified).toLocaleDateString()}
                        </p>
                      </div>

                      {/* Edit button - aligned to right center */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowClassEdit({ classId: cls.id });
                        }}
                        className="flex-shrink-0 p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Edit class"
                      >
                        <Edit3 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Images Panel */}
        <div className="w-2/3 bg-white shadow rounded-lg flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-gray-900">
                  Images
                </h2>
                {selectedClass && images.length > 0 && (
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectionModeImages}
                      onChange={() => setSelectionModeImages(v => !v)}
                      className="rounded border-purple-300 text-purple-600 shadow-sm focus:border-purple-300 focus:ring focus:ring-purple-200 focus:ring-opacity-50"
                    />
                    <span className="text-sm text-gray-500">
                      {images.length}
                    </span>
                  </label>
                )}
              </div>

              {/* Only show Upload button when NOT in selection mode */}
              {selectedClass && !selectionModeImages && (
                <div className="flex items-center gap-2">
                  {/* Upload button */}
                  <label
                    className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isIndexRebuilding
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-primary-600 text-white hover:bg-primary-700 cursor-pointer"
                    }`}
                  >
                    <Upload className="h-4 w-4" />
                    Upload
                    <input
                      type="file"
                      multiple
                      accept="image/*,.heic,.heif"
                      onChange={
                        isIndexRebuilding ? undefined : handleFileUpload
                      }
                      disabled={isIndexRebuilding}
                      className="hidden"
                    />
                  </label>
                </div>
              )}
            </div>

            {/* Select all and Delete row - shown when in selection mode */}
            {selectionModeImages && (
              <div className="mt-3 flex items-center justify-between">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectAllImages}
                    onChange={toggleAllImageSelection}
                    className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-600">Select all</span>
                </label>

                {selectedImagesCount > 0 && (
                  <button
                    onClick={deleteSelectedImages}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-red-200 text-sm font-medium rounded-lg text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete ({selectedImagesCount})
                  </button>
                )}
              </div>
            )}
          </div>

          <div
            className={`h-full overflow-y-auto transition-all duration-200 ${
              selectedClass && isDragOver
                ? "bg-primary-50/50"
                : ""
            }`}
            onDragEnter={selectedClass ? handleDragEnter : undefined}
            onDragLeave={selectedClass ? handleDragLeave : undefined}
            onDragOver={selectedClass ? handleDragOver : undefined}
            onDrop={selectedClass ? handleDrop : undefined}
          >
            {!selectedClass ? (
              <div className="p-6 text-center text-gray-500">
                <FileImage className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                <p>Select a class to view images</p>
              </div>
            ) : images.length === 0 ? (
              <div className="p-6 text-center">
                <FileImage
                  className={`mx-auto h-12 w-12 mb-2 ${isDragOver ? "text-primary-400" : "text-gray-400"}`}
                />
                {isDragOver ? (
                  <div className="flex items-center justify-center text-primary-600">
                    <Upload className="h-6 w-6 mr-2" />
                    <p className="font-medium">Drop images here to upload</p>
                  </div>
                ) : (
                  <p className="text-gray-500">
                    No images in this class - drag and drop images here to
                    upload
                  </p>
                )}
              </div>
            ) : (
              <div className="relative">
                {isDragOver && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary-50/90 backdrop-blur-sm border-4 border-dashed border-primary-400 rounded-lg m-4 pointer-events-none">
                    <div className="flex items-center gap-3 text-primary-600 font-semibold text-lg">
                      <Upload className="h-8 w-8" />
                      Drop images here to upload
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 p-4">
                {images.map((img) => (
                  <div
                    key={img.id}
                    className={`relative group cursor-pointer transition-all ${
                      selectionModeImages && img.selected ? "ring-2 ring-blue-400" : ""
                    }`}
                    onClick={() => {
                      if (selectionModeImages) {
                        toggleImageSelection(img.id);
                      } else {
                        setInfoOpenFor(null);
                      }
                    }}
                  >
                    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                      <AuthenticatedImage
                        imageUrl={img.imageUrl}
                        alt={`Image ${images.findIndex((i) => i.id === img.id) + 1}`}
                        className={`max-w-full max-h-full object-contain ${selectionModeImages ? "" : "cursor-pointer"}`}
                        onClick={(e) => {
                          if (!selectionModeImages) {
                            e.stopPropagation();
                            setShowImagePreview({ id: img.id, url: img.imageUrl });
                          }
                        }}
                        onError={(error) => {
                          console.error(
                            `Failed to load image: ${img.imageUrl}`,
                          );
                          console.error("Image data:", img);
                          console.error("Error:", error);
                        }}
                      />
                    </div>

                    {selectionModeImages && (
                      <div className="absolute top-2 left-2 pointer-events-none">
                        <input
                          type="checkbox"
                          checked={img.selected || false}
                          onChange={() => {}} // Handled by parent click
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 pointer-events-none"
                        />
                      </div>
                    )}

                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowImagePreview({ id: img.id, url: img.imageUrl })
                        }}
                        className="p-1 bg-black bg-opacity-50 text-white rounded"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!selectedClass) return;
                          try {
                            await apiService.setClassThumbnail(selectedClass, img.fileName);
                            await refreshThumbnailForClass(selectedClass);
                          } catch (err) {
                            console.error('Failed to set thumbnail', err);
                          }
                        }}
                        className="p-1 bg-black bg-opacity-50 text-white rounded"
                        title="Set as thumbnail"
                      >
                        <ImageIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          setInfoOpenFor(prev => prev === img.id ? null : img.id);
                          if (!embeddingInfo[img.id]) {
                            const itemId = img.fileName.replace(/\.[^.]+$/, '');
                            try {
                              const data = await apiService.getEmbeddingVector(itemId);
                              const vector = data.embedding_vector as number[];
                              setEmbeddingInfo(prev => ({ ...prev, [img.id]: { dims: vector.length, vector } }));
                            } catch (e) {
                              console.error('Failed to fetch embedding', e);
                            }
                          }
                        }}
                        className="p-1 bg-black bg-opacity-50 text-white rounded"
                        title="Info"
                      >
                        <Info className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Thumbnail highlight */}
                    {selectedClass && getClassThumbnail(selectedClass)?.path === img.fileName && (
                      <div className="pointer-events-none absolute inset-0 rounded-lg border-4 border-orange-500"></div>
                    )}

                    {infoOpenFor === img.id && (
                      <div className="absolute top-2 left-2 z-10 bg-white border rounded shadow p-2 max-w-[220px] text-xs" onClick={(e) => e.stopPropagation()}>
                        <div className="font-medium mb-1 truncate" title={img.fileName}>{img.fileName}</div>
                        {embeddingInfo[img.id] ? (
                          <div>
                            <div className="text-gray-600 mb-1">Embedding dims: {embeddingInfo[img.id].dims}</div>
                            <div className="font-mono text-[10px] text-gray-700 break-all">
                              {embeddingInfo[img.id].vector.slice(0, 8).map((v, i) => (i ? ', ' : '') + v.toFixed(3)).join('')}{embeddingInfo[img.id].vector.length > 8 ? ', ...' : ''}
                            </div>
                          </div>
                        ) : (
                          <div className="text-gray-500">Loading embedding...</div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Class Modal */}
      {showCreateClass && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Create New Class
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Class Name
                </label>
                <input
                  type="text"
                  value={newClassName}
                  onChange={(e) => setNewClassName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Love Ring Platinum"
                />
                <p className="text-sm text-gray-500 mt-1">
                  This will be used as both the folder name and display name
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateClass(false);
                  setNewClassName("");
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createClass}
                disabled={!newClassName.trim()}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                Create Class
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ZIP Rename Modal */}
      {showZipRenameModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Class Name Conflict
            </h3>

            {zipRenameError && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">{zipRenameError}</p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Class Name
                </label>
                <input
                  type="text"
                  value={zipFileName}
                  onChange={(e) => setZipFileName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter a different class name"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleZipRename();
                    }
                  }}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowZipRenameModal(false);
                  setZipFile(null);
                  setZipFileName("");
                  setZipRenameError(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleZipRename}
                disabled={!zipFileName.trim()}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                Retry Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImagePreview && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 cursor-pointer"
          onClick={() => setShowImagePreview(null)}
        >
          <div className="relative max-w-4xl max-h-4xl">
            {/* Image counter */}
            {images.length > 1 && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-3 py-1.5 rounded-lg">
                {currentImageIndex + 1} / {images.length}
              </div>
            )}
            <AuthenticatedImage
              imageUrl={showImagePreview.url}
              alt="Preview"
              className="max-w-full max-h-full cursor-default"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              onError={(error) => {
                console.error(
                  `Failed to load preview image: ${showImagePreview.url}`,
                );
                console.error("Error:", error);
              }}
            />
          </div>
        </div>
      )}

      {/* Upload Progress Modal */}
      {isUploading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {zipProgress.total > 1 ? "Importing ZIP Archives" : "Uploading Images"}
            </h3>

            {/* ZIP Progress Bar (only shown for multi-ZIP import) */}
            {zipProgress.total > 1 && (
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-700 mb-1">
                  <span className="font-medium">Archives</span>
                  <span>
                    {zipProgress.current + 1} / {zipProgress.total}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                    style={{
                      width: `${((zipProgress.current + 1) / zipProgress.total) * 100}%`,
                    }}
                  />
                </div>
                {zipProgress.currentZipName && (
                  <p className="text-xs text-gray-500 mt-1 truncate">
                    Current: {zipProgress.currentZipName}
                  </p>
                )}
              </div>
            )}

            {/* Images Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-700 mb-1">
                <span>{zipProgress.total > 1 ? "Images in current archive" : "Processing images..."}</span>
                <span>
                  {uploadProgress.current} / {uploadProgress.total}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${uploadProgress.total > 0 ? (uploadProgress.current / uploadProgress.total) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>

            {/* Recently uploaded images */}
            {uploadedImages.length > 0 && (
              <div className="max-h-32 overflow-y-auto">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Successfully processed:
                </p>
                <div className="space-y-1">
                  {uploadedImages.slice(-5).map((imageName, index) => (
                    <div
                      key={index}
                      className="text-sm text-green-600 flex items-center"
                    >
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2 flex-shrink-0"></div>
                      <span className="truncate">{imageName}</span>
                    </div>
                  ))}
                  {uploadedImages.length > 5 && (
                    <div className="text-sm text-gray-500">
                      ...and {uploadedImages.length - 5} more
                    </div>
                  )}
                </div>
              </div>
            )}

            <p className="text-sm text-gray-500 mt-4">
              Please wait while we process your images and create embeddings...
            </p>
          </div>
        </div>
      )}

      {/* Parquet Viewer Modal */}
      {showParquetViewer && parquetData && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl mx-4 h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Embedding Parquet Data
              </h3>
              <button
                onClick={() => setShowParquetViewer(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 p-6 overflow-hidden flex flex-col">
              {/* Summary Stats */}
              <div className="mb-4 space-y-2">
                <div className="flex items-center space-x-6 text-sm text-gray-600">
                  <div>
                    Total Records:{" "}
                    <span className="font-semibold text-gray-900">
                      {parquetData.total_records || 0}
                    </span>
                  </div>
                  <div>
                    Classes:{" "}
                    <span className="font-semibold text-gray-900">
                      {parquetData.unique_classes || 0}
                    </span>
                  </div>
                  {parquetData.file_size && (
                    <div>
                      File Size:{" "}
                      <span className="font-semibold text-gray-900">
                        {parquetData.file_size}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-6 text-sm text-gray-600">
                  <div>
                    Displaying:{" "}
                    <span className="font-semibold text-green-600">
                      All {parquetData.records?.length || 0} records
                    </span>
                  </div>
                </div>
              </div>

              {/* Data Table */}
              {parquetData.records && parquetData.records.length > 0 ? (
                <div className="flex-1 overflow-auto border rounded-lg">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                          Class
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                          #
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                          Created
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                          Embedding
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {parquetData.records.map((record: any, index: number) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-3 py-3 text-sm text-gray-900 truncate">
                            {record.class_name}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-600 font-mono">
                            #{index + 1}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-500 whitespace-nowrap">
                            {new Date(record.created_at).toLocaleDateString()}{" "}
                            {new Date(record.created_at).toLocaleTimeString(
                              [],
                              { hour: "2-digit", minute: "2-digit" },
                            )}
                          </td>
                          <td className="px-3 py-3 text-sm">
                            {record.embedding && record.embedding !== "N/A" ? (
                              <button
                                onClick={() =>
                                  viewEmbeddingVector(record.item_id)
                                }
                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                                title="Click to view full embedding vector"
                              >
                                RAW
                              </button>
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <Table className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p>No parquet data available</p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowParquetViewer(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Management Dialog */}
      <CategoryManagementDialog
        isOpen={showCategoryManagement}
        onClose={() => {
          setShowCategoryManagement(false);
          refreshCategories();
        }}
      />

      {/* Thumbnail Selection Dialog */}
      {showThumbnailSelection && (
        <ThumbnailSelectionDialog
          isOpen={true}
          onClose={() => setShowThumbnailSelection(null)}
          classId={showThumbnailSelection.classId}
          className={showThumbnailSelection.className}
        />
      )}

      {/* Class Edit Dialog */}
      {showClassEdit && (
        <ClassEditDialog
          classId={showClassEdit.classId}
          isOpen={true}
          onClose={() => setShowClassEdit(null)}
          onRenamed={(oldId, newId) => {
            // Update classes, selectedClass and refresh categories/thumbnails
            setClasses(prev => prev.map(c => c.id === oldId ? { ...c, id: newId, folderName: newId, displayName: newId } : c))
            if (selectedClass === oldId) selectClass(newId)
            setShowClassEdit(null)
            refreshCategories()
          }}
          onDeleted={(deletedId) => {
            setClasses(prev => prev.filter(c => c.id !== deletedId))
            if (selectedClass === deletedId) {
              const remaining = classes.filter(c => c.id !== deletedId)
              selectClass(remaining[0]?.id || null)
            }
            setShowClassEdit(null)
            refreshCategories()
          }}
        />
      )}
    </div>
  );
};

export default CatalogPage;
